import React, { useState, useCallback, useMemo } from 'react'
import { Grid } from '../Grid/Grid.js'
import { ScaledGrid } from '../Grid/ScaledGrid.js'
import { ShipDock } from '../ShipDock/ShipDock.js'
import { useAudio } from '../../hooks/useAudio.js'
import { useTheater } from '../../hooks/useTheater.js'
import { useIsWide } from '../../hooks/useIsWide.js'
import { isWebGLAvailable } from '../../utils/webgl.js'
import type { Ship, ShipType, Direction, Coord, CellState } from '@fleetduel/shared'
import { SHIP_DEFS, coordKey, getShipCells, validatePlacement, isWithinBounds } from '@fleetduel/shared'

const PlacementBoard3D = React.lazy(() =>
  import('../Scene3D/PlacementBoard3D.js').then(m => ({ default: m.PlacementBoard3D })),
)

interface PlacementProps {
  onConfirm: (ships: Ship[]) => void
}

export function Placement({ onConfirm }: PlacementProps) {
  const [ships, setShips] = useState<Ship[]>([])
  const [selectedShip, setSelectedShip] = useState<ShipType | null>(null)
  const [direction, setDirection] = useState<Direction>('horizontal')
  const [previewCoord, setPreviewCoord] = useState<Coord | null>(null)
  const { play } = useAudio()
  const { theaterId, config, terrainCells, blockedCells, palette } = useTheater()
  const isWide = useIsWide()

  const webgl = useMemo(() => isWebGLAvailable(), [])
  const [render3D, setRender3D] = useState(() => {
    if (!isWebGLAvailable()) return false
    try { return localStorage.getItem('fleetduel-render3d') !== 'false' } catch { return true }
  })
  const toggleRender3D = useCallback(() => {
    setRender3D(prev => {
      const next = !prev
      try { localStorage.setItem('fleetduel-render3d', String(next)) } catch {}
      return next
    })
  }, [])

  const placedTypes = new Set(ships.map(s => s.type))

  // Compute preview cells (valid = green, invalid = red)
  const previewCells = useMemo(() => {
    if (!selectedShip || !previewCoord) return new Map<string, 'valid' | 'invalid'>()

    const def = SHIP_DEFS[selectedShip]
    const ship: Ship = { type: selectedShip, origin: previewCoord, direction, size: def.size }
    const cells = getShipCells(ship)

    const existingCells = new Set(ships.flatMap(s => getShipCells(s).map(coordKey)))
    const hasOverlap = cells.some(c => existingCells.has(coordKey(c)))
    const hitsTerrain = cells.some(c => blockedCells.has(coordKey(c)))
    const isValid = isWithinBounds(ship) && !hasOverlap && !hitsTerrain

    const result = new Map<string, 'valid' | 'invalid'>()
    for (const cell of cells) {
      result.set(coordKey(cell), isValid ? 'valid' : 'invalid')
    }
    return result
  }, [selectedShip, previewCoord, direction, ships, blockedCells])

  const tryPlaceShip = useCallback((coord: Coord) => {
    if (!selectedShip) return false

    const def = SHIP_DEFS[selectedShip]
    const newShip: Ship = { type: selectedShip, origin: coord, direction, size: def.size }

    if (!isWithinBounds(newShip)) return false

    const newCells = new Set(getShipCells(newShip).map(coordKey))
    for (const key of newCells) {
      if (blockedCells.has(key)) return false
    }
    for (const existing of ships) {
      for (const cell of getShipCells(existing)) {
        if (newCells.has(coordKey(cell))) return false
      }
    }

    setShips(prev => [...prev, newShip])
    setSelectedShip(null)
    setPreviewCoord(null)
    play('ship-place', 'ui')
    return true
  }, [selectedShip, direction, ships, play, blockedCells])

  // Pick up an already-placed ship (removing it from the board) so its
  // placement can be re-done. Restores its orientation for a natural edit.
  const pickUpShip = useCallback((type: ShipType) => {
    const existing = ships.find(s => s.type === type)
    if (existing) {
      setShips(prev => prev.filter(s => s.type !== type))
      setDirection(existing.direction)
    }
    setSelectedShip(type)
    setPreviewCoord(null)
  }, [ships])

  const handleCellClick = useCallback((coord: Coord) => {
    if (!selectedShip) {
      // No ship in hand: clicking one already on the board picks it up to edit
      const hit = ships.find(s => getShipCells(s).some(c => coordKey(c) === coordKey(coord)))
      if (hit) {
        pickUpShip(hit.type)
        play('ship-place', 'ui')
      }
      return
    }

    // Desktop: hover already set previewCoord, so click on same coord → place
    // Mobile: first tap sets preview, second tap on same coord → place
    if (previewCoord && coordKey(previewCoord) === coordKey(coord)) {
      tryPlaceShip(coord)
    } else {
      setPreviewCoord(coord)
    }
  }, [selectedShip, previewCoord, tryPlaceShip, ships, pickUpShip, play])

  const handleCellHover = useCallback((coord: Coord) => {
    if (!selectedShip) return
    setPreviewCoord(coord)
  }, [selectedShip])

  const handleGridLeave = useCallback(() => {
    setPreviewCoord(null)
  }, [])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setDirection(d => d === 'horizontal' ? 'vertical' : 'horizontal')
  }, [])

  const handleSelectShip = useCallback((type: ShipType) => {
    pickUpShip(type)
  }, [pickUpShip])

  const canConfirm = ships.length === 3 && validatePlacement(ships, blockedCells).length === 0
  const cellStates = new Map<string, CellState>()

  // Board capped by viewport height (dvh) so board + dock + controls always
  // fit one screen on mobile without scrolling.
  const boardWidth = isWide
    ? 'min(560px, calc(100vw - 340px), calc(100dvh - 200px))'
    : 'min(92vw, 560px, max(240px, calc(100dvh - 310px)))'

  const board = (
    <div style={{ width: boardWidth }} onContextMenu={handleContextMenu}>
      {render3D ? (
        <React.Suspense fallback={<div style={{ width: '100%', aspectRatio: '1', borderRadius: 8, backgroundColor: '#13315C' }} />}>
          <PlacementBoard3D
            ships={ships}
            selectedShip={selectedShip}
            direction={direction}
            terrainCells={terrainCells}
            blockedCells={blockedCells}
            theaterId={theaterId}
            onCellClick={handleCellClick}
            onCellHover={handleCellHover}
          />
        </React.Suspense>
      ) : (
        <ScaledGrid>
          <Grid
            cellStates={cellStates}
            isOwnBoard={true}
            ships={ships}
            previewCells={previewCells}
            terrainCells={terrainCells}
            waterTint={palette.waterTint}
            onCellClick={handleCellClick}
            onCellHover={handleCellHover}
            onGridLeave={handleGridLeave}
            onContextMenu={handleContextMenu}
          />
        </ScaledGrid>
      )}
    </div>
  )

  const dock = (
    <ShipDock
      placedShips={placedTypes}
      selectedShip={selectedShip}
      direction={direction}
      onSelectShip={handleSelectShip}
      onToggleDirection={() => setDirection(d => d === 'horizontal' ? 'vertical' : 'horizontal')}
      onConfirm={() => { play('button-press', 'ui'); onConfirm(ships) }}
      canConfirm={canConfirm}
      vertical={isWide}
      compact={!isWide}
    />
  )

  const controls = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
      {selectedShip && (
        <p style={{ color: '#90a4ae', fontSize: 12, margin: 0, textAlign: 'center' }}>
          {previewCoord
            ? 'Click to place · Right-click to rotate'
            : 'Hover a cell to preview · Right-click to rotate'}
        </p>
      )}
      {webgl && (
        <button
          onClick={toggleRender3D}
          style={{
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 700,
            border: '1px solid rgba(141, 169, 196, 0.4)',
            borderRadius: 6,
            backgroundColor: '#13315C',
            color: '#8DA9C4',
            cursor: 'pointer',
            letterSpacing: 1,
          }}
        >
          {render3D ? 'VIEW: 3D' : 'VIEW: 2D'}
        </button>
      )}
    </div>
  )

  return (
    <div className="screen-fill" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: isWide ? 16 : 8,
      padding: isWide ? 16 : 12,
    }}>
      <h2 style={{ color: '#4fc3f7', margin: 0, fontSize: isWide ? 24 : 18 }}>Place Your Fleet</h2>

      {/* Terrain legend (ART_GUIDE_v3 §6 — placement screen) */}
      {terrainCells.size > 0 && (
        <p style={{ color: '#8DA9C4', fontSize: isWide ? 12 : 11, margin: 0, textAlign: 'center', maxWidth: '92vw' }}>
          {config.name}: {config.description} Ships cannot be placed on terrain.
        </p>
      )}

      {isWide ? (
        // Wide: dock as a left column so ships are reachable without scrolling.
        // Fixed column width so the hint text changing length never reflows
        // the board's position.
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'center', gap: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, width: 220, flexShrink: 0 }}>
            {dock}
            {controls}
          </div>
          {board}
        </div>
      ) : (
        <>
          {board}
          {dock}
          {controls}
        </>
      )}
    </div>
  )
}
